import React, { useState, useEffect } from 'react';
import { colors } from 'shared/styles';
import styled from 'styled-components';

import { GqlRewardV2 } from 'types/graphql';
import useCart from 'hooks/use-cart';
import Button from '../../core/button';

type RewardsProps = {
  rewards: Array<GqlRewardV2>;
  onApplyReward?: (cost: number, copy: string, id: string) => void;
  onUndoReward?: () => void;
  rewardsBalanceNumber: number;
};

export const DposApplyRewardsCheckout = ({
  rewards = [],
  onApplyReward,
  onUndoReward,
  rewardsBalanceNumber,
}: RewardsProps): JSX.Element => {
  const Cart = useCart();
  const { appliedRewards } = Cart;
  const availableRewards = rewards.filter(({ available }) => available);
  const [appliedRewardId, setAppliedRewardId] = useState<string | null>(null);

  const handleApplyReward = (cost: number, copy: string, id: string): void => {
    if (onApplyReward) {
      onApplyReward(cost, copy, id);
      setAppliedRewardId(id);
    }
  };

  const handleUndoReward = (): void => {
    if (onUndoReward) {
      onUndoReward();
      setAppliedRewardId(null);
    }
  };

  useEffect(() => {
    if (appliedRewardId && !appliedRewards.some((reward) => reward.id === appliedRewardId)) {
      setAppliedRewardId(null);
    }
  }, [appliedRewards, appliedRewardId]);

  return (
    <Container>
      <HeaderText>The following reward options are eligible for this order</HeaderText>
      {availableRewards.length > 0 ? (
        availableRewards.map(({ copy, cost, id }) => {
          const isApplied = appliedRewardId === id;
          const isDisabled = appliedRewardId !== null && !isApplied;
          const tooExpensive = cost > rewardsBalanceNumber;

          return (
            <RewardCard key={`${id}-${copy ?? ''}`} isDisabled={isDisabled}>
              <RewardInfo>
                <RewardTitle isDisabled={isDisabled}>{copy}</RewardTitle>
                <PointsRow>
                  <PointsCost isDisabled={isDisabled}>{cost} pts</PointsCost>
                  {isApplied && (
                    <AppliedContainer>
                      <CheckIcon>✓</CheckIcon>
                      <AppliedText>Applied</AppliedText>
                    </AppliedContainer>
                  )}
                </PointsRow>
              </RewardInfo>
              {isApplied ? (
                <Button onClick={handleUndoReward}>UNDO</Button>
              ) : (
                <Button
                  onClick={() => handleApplyReward(cost, copy ?? '', id)}
                  disabled={isDisabled || tooExpensive}
                  // isDisabled={isDisabled || tooExpensive}
                >
                  APPLY
                </Button>
              )}
            </RewardCard>
          );
        })
      ) : (
        <NoRewardsAvailable>No eligible rewards at this time</NoRewardsAvailable>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const HeaderText = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 8px 0;
  font-weight: 400;
`;

const RewardCard = styled.div<{ isDisabled?: boolean }>`
  background-color: transparent;
  padding: 12px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  opacity: ${({ isDisabled }) => (isDisabled ? 0.5 : 1)};
  transition: opacity 0.2s ease;
`;

const RewardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const RewardTitle = styled.h3<{ isDisabled?: boolean }>`
  font-size: 16px;
  font-weight: 600;
  color: ${({ isDisabled }) => (isDisabled ? '#9ca3af' : '#1f2937')};
  margin: 0;
  line-height: 1.25;
`;

const PointsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AppliedContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CheckIcon = styled.span`
  color: #10b981;
  font-weight: bold;
  font-size: 14px;
`;

const AppliedText = styled.span`
  color: #10b981;
  font-size: 14px;
  font-weight: 500;
`;

const PointsCost = styled.p<{ isDisabled?: boolean }>`
  font-size: 14px;
  color: ${({ isDisabled }) => (isDisabled ? '#d1d5db' : '#6b7280')};
  margin: 0;
  font-weight: 400;
`;

const NoRewardsAvailable = styled.div`
  color: ${colors.blueGrey};
  text-align: center;
  padding: 24px;
  font-style: italic;
`;
